import React from "react";
import classes from "./LabelComponent.scss";
import PlannerIcon from "UnitPlans/components/LabelComponent/PlannerIcon";
import CommentButton from "./components/CommentButton";
import AHFCompletedBanner from "ProjectDetails/components/AHFCompletedBanner";
import { IconButton, Tooltip, Button } from "@toddle-edu/ds-web";
import {
  DownloadOutlined,
  HelpBulbOutlined,
  DashedCircleOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import ACLStore from "lib/aclStore";

import { useI18n } from "Hooks";

const isPrintEnabled = !ACLStore.can("FeatureFlag:PreventPrintAndScreenshots");

const LabelComponent = props => {
  const {
    icon,
    label,
    subText,
    showExtraRightButtonComponent,
    showComment,
    totalCommentCount,
    unreadCommentCount,
    onCommentClick,
    isCommentActive,
    commentIconSize,
    testId,
    showHelper,
    isHelpActive,
    onHelpClick,
    helpButtonId,
    isPageHeader,
    isSectionHeader,
    isFieldHeader,
    showPrint,
    onPrintClick,
    customLabelStyles = {},
    subTextClass,
    formName,
    toggleCompleteButtonProps = {},
    renderBottomComp,
    extraLabelComponent,
    showDelete,
    extraRightButtonComponent,
  } = props;

  const { t } = useI18n();

  if (!label) return null;

  const {
    isComplete,
    fieldUid,
    disabled,
    updateInputField,
  } = toggleCompleteButtonProps;

  const toggleCompleteAction = () => {
    updateInputField({
      params: {
        [fieldUid]: "",
      },
      extraParams: {
        isComplete: !isComplete,
      },
    });
  };

  const containerClass = classNames(classes.container, {
    [classes.containerWithSubText]: !!subText,
    [classes.pageHeaderContainer]: isPageHeader,
    [classes.sectionHeaderContainer]: isSectionHeader,
    [classes.fieldHeaderContainer]: isFieldHeader,
  });

  const labelClasses = classNames(classes.textPrimary, classes.label, {
    "text-heading-5": isPageHeader,
    "text-label": isFieldHeader,
  });

  const subTextClasses = classNames(classes.textSecondary, {
    "text-body": isPageHeader && !subTextClass,
    "text-body-s": (isSectionHeader || isFieldHeader) && !subTextClass,
    [subTextClass]: !!subTextClass,
  });

  const leftContainerClasses = classNames(classes.leftContainer, {
    [classes.leftContainerWithSubText]: !!subText,
  });

  const showRightContainer =
    (showPrint && isPrintEnabled) ||
    showExtraRightButtonComponent ||
    showComment ||
    showHelper;

  return (
    <>
      <div className={containerClass} style={customLabelStyles}>
        <div className={leftContainerClasses}>
          {icon ? <PlannerIcon icon={icon} label={label} /> : null}
          <div className={classes.textContainer}>
            {label ? (
              <div className={classes.labelContainer}>
                <h6
                  className={labelClasses}
                  dangerouslySetInnerHTML={{
                    __html: label,
                  }}
                  dir="auto"
                />
                {extraLabelComponent ?? null}
              </div>
            ) : null}
            {subText ? (
              <div
                className={subTextClasses}
                dangerouslySetInnerHTML={{
                  __html: subText,
                }}
              />
            ) : null}
          </div>
        </div>
        {showRightContainer ? (
          <div className={classes.rightContainer}>
            {showPrint && isPrintEnabled ? (
              <Tooltip
                tooltip={t("common:download_label", {
                  label: t(formName),
                })}
              >
                <IconButton
                  icon={<DownloadOutlined variant="subtle" />}
                  type="outlined"
                  variant="neutral"
                  onClick={onPrintClick}
                  testId={`${testId}-download`}
                />
              </Tooltip>
            ) : null}

            {showExtraRightButtonComponent && !isComplete ? (
              <Button
                disabled={disabled}
                icon={<DashedCircleOutlined />}
                type="outlined"
                variant="neutral"
                onClick={toggleCompleteAction}
                testId={`${testId}-markComplete`}
              >
                {t("project:mark_as_complete")}
              </Button>
            ) : null}
            {/* {showExtraRightButtonComponent ? extraRightButtonComponent : null} */}
            {showDelete && extraRightButtonComponent && (
              <div className={classes.extraButton}>
                {extraRightButtonComponent}
              </div>
            )}
            {showComment ? (
              <CommentButton
                totalCommentCount={totalCommentCount}
                unreadCommentCount={unreadCommentCount}
                onCommentClick={onCommentClick}
                isCommentActive={isCommentActive}
                commentIconSize={commentIconSize}
                testId={testId}
                label={label}
              />
            ) : null}
            {showHelper ? (
              <IconButton
                icon={<HelpBulbOutlined variant="subtle" />}
                type="plain"
                variant="neutral"
                isActivated={isHelpActive}
                onClick={onHelpClick}
                id={helpButtonId}
                testId={`${testId}-help`}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      {showExtraRightButtonComponent && isComplete ? (
        <AHFCompletedBanner
          title={t(
            "common:label_has_been_marked_as_complete_with_exclamation",
            {
              label: t(formName),
            }
          )}
          onButtonClick={toggleCompleteAction}
          disabled={disabled}
        />
      ) : null}

      {renderBottomComp ? renderBottomComp : null}
    </>
  );
};

export default LabelComponent;
